import { cva, type VariantProps } from "class-variance-authority";

/**
 * Branded type-scale variants (see ./typography.tsx for the components).
 *
 * The scale utilities (`text-display-xl` … `text-nav`) are registered in
 * app/styles/themes/theme-inline.css and set font-size + line-height +
 * font-weight + letter-spacing together. Font-FAMILY is inherited so a heading
 * renders in whatever `font-sans` / `font-serif` its context supplies.
 */

export const headingVariants = cva("text-balance text-foreground", {
	variants: {
		/** Role → scale step. display-* are page/section heroes; title-* are
		   section/card/list headers. */
		variant: {
			"display-xl": "text-display-xl",
			"display-lg": "text-display-lg",
			"display-md": "text-display-md",
			"display-sm": "text-display-sm",
			"title-lg": "text-title-lg",
			"title-md": "text-title-md",
			"title-sm": "text-title-sm",
		},
	},
	defaultVariants: { variant: "title-md" },
});

export const textVariants = cva("", {
	variants: {
		/** Role → scale step for non-heading text. display-* are available for
		    large display VALUES (stat numbers, prices, plan names) that are not
		    document headings — use <Heading> when it is a real heading. */
		variant: {
			"display-xl": "text-display-xl",
			"display-lg": "text-display-lg",
			"display-md": "text-display-md",
			"display-sm": "text-display-sm",
			"title-lg": "text-title-lg",
			"title-md": "text-title-md",
			"title-sm": "text-title-sm",
			"body-md": "text-body-md",
			"body-sm": "text-body-sm",
			caption: "text-caption",
			overline: "text-overline uppercase",
			code: "font-mono text-code",
			button: "text-button",
			nav: "text-nav",
		},
		/** Ink tone from the extended text ramp. */
		tone: {
			default: "text-foreground",
			strong: "text-body-strong",
			body: "text-body",
			muted: "text-muted-foreground",
			"muted-soft": "text-muted-soft",
			primary: "text-primary",
		},
	},
	defaultVariants: { variant: "body-md", tone: "body" },
});

export type HeadingVariantProps = VariantProps<typeof headingVariants>;
export type TextVariantProps = VariantProps<typeof textVariants>;
